/**
 * Batch Job Monitor
 *
 * Real-time monitoring dashboard for question generation batch jobs.
 * Displays progress, status, and detailed information about each generation task.
 *
 * Date: 2025-11-15
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  AlertCircle,
  Package,
  ArrowLeft,
  RefreshCw,
  StopCircle,
  TrendingUp,
  Eye,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

interface BatchJob {
  id: string;
  sectorId: number;
  sectorName: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  totalItems: number;
  completedItems: number;
  failedItems: number;
  skippedItems: number;
  totalQuestionsGenerated: number;
  totalQuestionsValidated: number;
  progressPercent: number;
  config: any;
  startedAt?: string;
  completedAt?: string;
  estimatedCompletionAt?: string;
  durationMs?: number;
  errorMessage?: string;
  createdAt: string;
}

interface BatchJobItem {
  id: string;
  wheelId: string;
  wheelName: string;
  questionType: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  questionsGenerated: number;
  questionsValidated: number;
  questionsFailed: number;
  parameters: any;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  errorMessage?: string;
  executionOrder: number;
}

const STATUS_STYLES = {
  pending: {
    icon: Clock,
    color: 'text-gray-500',
    bg: 'bg-gray-100',
    badge: 'secondary',
  },
  running: {
    icon: Loader2,
    color: 'text-blue-500',
    bg: 'bg-blue-100',
    badge: 'default',
  },
  completed: {
    icon: CheckCircle2,
    color: 'text-green-500',
    bg: 'bg-green-100',
    badge: 'success',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-500',
    bg: 'bg-red-100',
    badge: 'destructive',
  },
  cancelled: {
    icon: StopCircle,
    color: 'text-orange-500',
    bg: 'bg-orange-100',
    badge: 'warning',
  },
  skipped: {
    icon: AlertCircle,
    color: 'text-yellow-500',
    bg: 'bg-yellow-100',
    badge: 'warning',
  },
};

export default function BatchJobMonitor() {
  const { sectorId, jobId } = useParams<{ sectorId: string; jobId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<BatchJob | null>(null);
  const [items, setItems] = useState<BatchJobItem[]>([]);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadJobStatus();

    // Poll every 2 seconds if job is running
    const interval = setInterval(() => {
      if (job?.status === 'running' || job?.status === 'pending') {
        loadJobStatus();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [jobId, job?.status]);

  const loadJobStatus = async () => {
    try {
      const response = await authFetch(
        `/api/sectors/${sectorId}/question-generation/batch-jobs/${jobId}`
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load job status');
      }

      setJob(data.job);
      setItems(data.items || []);
    } catch (error: any) {
      console.error('Error loading job status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load job status',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = async () => {
    if (!window.confirm('Are you sure you want to cancel this batch job?')) {
      return;
    }

    try {
      setCancelling(true);

      const response = await authFetch(
        `/api/sectors/${sectorId}/question-generation/batch-jobs/${jobId}/cancel`,
        {
          method: 'POST',
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to cancel job');
      }

      toast({
        title: 'Job Cancelled',
        description: 'Batch job has been cancelled',
      });

      loadJobStatus();
    } catch (error: any) {
      console.error('Error cancelling job:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel job',
        variant: 'destructive',
      });
    } finally {
      setCancelling(false);
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  const getQuestionTypeLabel = (questionType: string): string => {
    const labels: Record<string, string> = {
      'PC-PA': 'Product Category - Product Attribute',
      'PC-PB': 'Product Category - Perceived Benefit',
      'PC-BP': 'Product Category - Buyer Persona',
      'PC-PA-PROD': 'Product-Specific Attribute',
      'PC-PB-PROD': 'Product-Specific Benefit',
      'PC-BP-PROD': 'Product-Specific Use-Case',
      'PB-RANK': 'Benefit Ranking',
      'BP-RANK': 'Buyer Persona Ranking',
      'BP-PC-PB': 'Buyer Persona - Category Benefit',
      'PC-PB-CMP': 'Category Benefit Comparison',
      'PC-PB-CMP-NPROD': 'Category Benefit Comparison (N-Product)',
      'PC-BP-PB-CMP': 'Category Buyer Persona Benefit Comparison',
      'PC-BP-PB-CMP-NPROD': 'Category Buyer Persona Benefit Comparison (N-Product)',
      'PC-TR': 'Product Category - Transaction',
      'category': 'Category Questions',
    };
    return labels[questionType] || questionType;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!job) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Job not found</AlertDescription>
      </Alert>
    );
  }

  const StatusIcon = STATUS_STYLES[job.status].icon;
  const isActive = job.status === 'running' || job.status === 'pending';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/sectors/${sectorId}/batch-generation`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Batch Job Monitor</h1>
            <p className="text-muted-foreground mt-1">
              {job.sectorName} - Job {job.id.substring(0, 8)}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          {job.status === 'completed' && items.length > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                const firstWheel = items[0];
                navigate(`/question-wheels/${firstWheel.wheelId}/debug?sector=${sectorId}`);
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              Evaluate Questions
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={loadJobStatus}
            disabled={isActive}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {isActive && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCancelJob}
              disabled={cancelling}
            >
              <StopCircle className="h-4 w-4 mr-2" />
              {cancelling ? 'Cancelling...' : 'Cancel Job'}
            </Button>
          )}
        </div>
      </div>

      {/* Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StatusIcon
              className={cn(
                'h-5 w-5',
                STATUS_STYLES[job.status].color,
                job.status === 'running' && 'animate-spin'
              )}
            />
            Job Status: {job.status.toUpperCase()}
          </CardTitle>
          <CardDescription>
            Created {formatTimestamp(job.createdAt)}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Progress</span>
              <span className="text-muted-foreground">
                {job.completedItems + job.failedItems} / {job.totalItems} tasks
              </span>
            </div>
            <Progress value={job.progressPercent} className="h-3" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{job.progressPercent}% Complete</span>
              {job.estimatedCompletionAt && isActive && (
                <span>
                  ETA: {formatTimestamp(job.estimatedCompletionAt)}
                </span>
              )}
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid gap-4 md:grid-cols-4">
            <div className="text-center p-4 rounded-lg bg-green-50">
              <div className="text-2xl font-bold text-green-600">
                {job.completedItems}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Completed
              </div>
            </div>
            <div className="text-center p-4 rounded-lg bg-red-50">
              <div className="text-2xl font-bold text-red-600">
                {job.failedItems}
              </div>
              <div className="text-sm text-muted-foreground mt-1">Failed</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-blue-50">
              <div className="text-2xl font-bold text-blue-600">
                {job.totalQuestionsGenerated}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Questions Generated
              </div>
            </div>
            <div className="text-center p-4 rounded-lg bg-purple-50">
              <div className="text-2xl font-bold text-purple-600">
                {formatDuration(job.durationMs)}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Duration
              </div>
            </div>
          </div>

          {/* Error Message */}
          {job.errorMessage && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{job.errorMessage}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Task Details */}
      <Card>
        <CardHeader>
          <CardTitle>Generation Tasks</CardTitle>
          <CardDescription>
            Detailed status of each generation task
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Wheel</TableHead>
                  <TableHead>Question Type</TableHead>
                  <TableHead className="text-right">Generated</TableHead>
                  <TableHead className="text-right">Validated</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const ItemStatusIcon = STATUS_STYLES[item.status].icon;
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {item.executionOrder}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_STYLES[item.status].badge as any}>
                          <ItemStatusIcon
                            className={cn(
                              'h-3 w-3 mr-1',
                              item.status === 'running' && 'animate-spin'
                            )}
                          />
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {item.wheelName}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge variant="outline">{item.questionType}</Badge>
                          <div className="text-xs text-muted-foreground">
                            {getQuestionTypeLabel(item.questionType)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {item.questionsGenerated || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.questionsValidated || '-'}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {item.questionsFailed || '-'}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatDuration(item.durationMs)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {items.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No tasks found
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
